/**
 * The studio's initials, in the accent square (D-517).
 *
 * <b>It said `CD` and it should say `CDA`</b> — reported off a phone
 * screenshot. The mark is not the point of this file; the fact that fixing two
 * letters meant editing **three** components is. `App`, `AuthLayout` and
 * `PublicShell` each carried their own copy, byte-identical but for the size,
 * and they had already drifted in the corner radius: two `rounded-lg`, one
 * `rounded-xl`, which nobody had compared because there was nowhere to compare
 * them.
 *
 * So the initials live in one place. The next correction is one edit, and the
 * three shells cannot disagree about the studio's own badge.
 *
 * <b>Not a `Setting`, deliberately.</b> A-16 keeps Tonya's *words* out of the
 * code, and this is her mark rather than her copy — the public shell already
 * prefers an uploaded `site.logo` image over it (§4.7), so the studio's real
 * branding is data and this is the fallback that shows before she uploads one.
 */
export default function StudioMark({ size = 'md' }: { size?: 'sm' | 'md' | 'lg' }) {
  /*
   * Three letters in a square that used to hold two, so the type is set per
   * size rather than inherited. `tracking-tight` on the two smaller ones is
   * what keeps `CDA` from touching the corners at 32px — measured, not
   * guessed; the italic face is what makes the third letter tight.
   */
  const box = {
    sm: 'size-8 rounded-lg text-[12px] tracking-tight',
    md: 'size-9 rounded-lg text-[13px] tracking-tight',
    lg: 'size-11 rounded-xl text-base',
  }[size]

  return (
    <span
      aria-hidden="true"
      className={`grid shrink-0 place-items-center bg-accent font-display font-bold italic text-on-accent ${box}`}
    >
      CDA
    </span>
  )
}
